import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebaseConfig";
import {
  getDocs,
  collection,
  updateDoc,
  doc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { useMsal } from "@azure/msal-react";
import Sidebar from "./SideBar/Sidebar";
import UsersSection from "../AdminPage/User Admin/UsersSection";
import ModulesSection from "./Modules Admin/ModulesSection";
import UserManagement from "./UserManagement/UserManagement";
import ModuleModal from "./Modules Admin/ModuleModal";
import AddModuleModal from "./Modules Admin/AddModuleModal";
import "./AdminPage.css";
import "../AdminPage/SideBar/Sidebar.css";
import NavBar from "../Navbar";
import coreLogo from "../../assets/core_logo_white.svg";
import { Navigate } from "react-router-dom";
import Swal from "sweetalert2";

function AdminPage({ userRole, userInformations }) {
  const [selectedSection, setSelectedSection] = useState("users"); // Inicialize com a seção de usuários
  const [modulesData, setModulesData] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [isAddingModule, setIsAddingModule] = useState(false);
  const [active, setActive] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const { accounts } = useMsal();

  const customStyles = {
    navbar: { backgroundColor: "#0067FF" },
    navbarText: { color: "white" },
    userInitialNavbar: { backgroundColor: "#004981" },
    userEllipse: { backgroundColor: "white" },
    userRectangle: { backgroundColor: "white" },
    arrowIcon: { color: "#0067FF" },
    dropdownMenu: { backgroundColor: "white" },
    dropdownItem: { color: "black" },
    backgroundRectangleNavbar: { backgroundColor: "white" },
    dropdownDivider: { backgroundColor: "#a4a4a4" },
  };

  const loadModulesData = async () => {
    const modulesSnapshot = await getDocs(collection(db, "modules"));
    const modulesList = modulesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setModulesData(modulesList);
  };

  useEffect(() => {
    loadModulesData(); // Carregar dados dos módulos ao montar o componente
  }, []);

  useEffect(() => {
    if (selectedSection === "modules") {
      loadModulesData();
    }
  }, [selectedSection]);

  useEffect(() => {
    // Mova a lógica de definição de "active" para o useEffect
    if (userInformations && userInformations.isActive !== undefined) {
      setActive(userInformations.isActive);
    }
  }, [userInformations]);

  const handleSaveModule = async (updatedModule) => {
    try {
      setIsSaving(true);

      // 1. Salvar/Atualizar o módulo no Firestore
      let moduleRef;

      if (updatedModule.id) {
        // Atualizar módulo existente
        moduleRef = doc(db, "modules", updatedModule.id);
        await updateDoc(moduleRef, {
          pageName: updatedModule.pageName || " ",
          pageURL: updatedModule.pageURL || " ",
          isActive: updatedModule.isActive ?? true,
          priority: updatedModule.priority || 20,
          pageIconUrl: updatedModule.icon || " ",
          description: updatedModule.description || " ",
          isVisibleForAll: updatedModule.isVisibleForAll || false,
          subsidiaries: updatedModule.subsidiaries || [],
        });
      } else {
        // Criar novo módulo
        if (!updatedModule.pageName) {
          return;
        }
        moduleRef = doc(db, "modules", updatedModule.pageName);
        await setDoc(moduleRef, {
          pageName: updatedModule.pageName,
          pageURL: updatedModule.pageURL || " ",
          isActive: updatedModule.isActive ?? true,
          pageIconUrl: updatedModule.icon || " ",
          priority: updatedModule.priority || 20,
          description: updatedModule.description || " ",
          isVisibleForAll: updatedModule.isVisibleForAll || false,
          subsidiaries: updatedModule.subsidiaries || [],
        });
      }

      loadModulesData();
      setSelectedModule(null);
      setIsAddingModule(false);
    } catch (error) {
      console.error("[handleSaveModule] Erro:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteModule = async (module) => {
    setIsSaving(true);
    const result = await Swal.fire({
      title: "Delete Confirmation",
      text: `Are you sure you want to delete the module "${module.id}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "custom-popup", // Personaliza o pop-up
        title: "custom-title", // Personaliza o título
        confirmButton: "custom-confirm-button", // Personaliza o botão de confirmação
        cancelButton: "custom-cancel-button", // Personaliza o botão de cancelamento
      },
    });

    if (!result.isConfirmed) {
      setIsSaving(false);
      return;
    }
    try {
      const enterprisesRef = collection(db, "regional_enterprises");
      const querySnapshot = await getDocs(enterprisesRef);

      const removeModulePromises = [];

      querySnapshot.forEach((docSnapshot) => {
        const enterpriseData = docSnapshot.data();

        if (enterpriseData.users) {
          const updatedUsers = {};

          // Percorre cada usuário para verificar se o módulo está presente e remover
          Object.entries(enterpriseData.users).forEach(([email, userData]) => {
            if (userData.accessibleModules) {
              const hasModule = userData.accessibleModules.some(
                (accessibleModule) => accessibleModule.id === module.id
              );

              if (hasModule) {
                updatedUsers[email] = {
                  ...userData,
                  accessibleModules: userData.accessibleModules.filter(
                    (accessibleModule) => accessibleModule.id !== module.id
                  ),
                };
              }
            }
          });

          // Se houve modificações, atualiza o documento do Firestore
          if (Object.keys(updatedUsers).length > 0) {
            const enterpriseRef = doc(
              db,
              "regional_enterprises",
              docSnapshot.id
            );
            removeModulePromises.push(
              updateDoc(enterpriseRef, {
                users: {
                  ...enterpriseData.users,
                  ...updatedUsers,
                },
              })
            );
          }
        }
      });

      // Aguarda todas as atualizações dos usuários
      await Promise.all(removeModulePromises);

      // Em seguida, deletamos o módulo do Firestore
      const moduleRef = doc(db, "modules", module.id);
      await deleteDoc(moduleRef);

      // Atualiza a lista de módulos e limpa a seleção
      loadModulesData();
      setSelectedModule(null);
    } catch (error) {
      console.warn(
        "[handleDeleteModule] Error ao deletar modulo no Firestore: ",
        error
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Checar se o usuário está ativo, e se não estiver, redirecionar para a página de "Unauthorized"
  if (active === false) {
    return <Navigate to="/unauthorized" />;
  }

  return (
    <div className="admin-container">
      <NavBar
        userInformations={userInformations}
        customStylesNavbar={customStyles}
        logoCore={coreLogo}
        navbarText={"Admin mode | "}
        pageMode={"admin"}
      />
      <div className="admin-main">
        <Sidebar
          userRole={userRole}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          navigate={navigate}
        />
        <div className="content">
          {selectedSection === "users" && (
            <UsersSection
              userRole={userRole}
              accounts={accounts}
              modulesData={modulesData}
              userInformations={userInformations}
            />
          )}
          {userRole === "admin_global" && selectedSection === "modules" && (
            <ModulesSection
              modulesData={modulesData}
              setSelectedModule={setSelectedModule}
              openAddModuleModal={() => setIsAddingModule(true)}
              userRole={userRole}
            />
          )}
          {selectedSection === "user_management" && (
            <UserManagement
              userRole={userRole}
              accounts={accounts}
              userInformations={userInformations}
            />
          )}
          {selectedModule && (
            <ModuleModal
              module={selectedModule}
              onClose={() => setSelectedModule(null)}
              onSave={handleSaveModule}
              onDelete={handleDeleteModule}
              isSaving={isSaving}
              userRole={userRole}
            />
          )}
          {isAddingModule && (
            <AddModuleModal
              onClose={() => setIsAddingModule(false)}
              onSave={handleSaveModule}
              isSaving={isSaving}
              userRole={userRole}
            />
          )}
        </div>
      </div>
      <div className="orange-divider-adminpage"></div>
    </div>
  );
}

export default AdminPage;
